import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { CreditCard, Clock, CheckCircle2, Phone, MessageSquare, Banknote } from 'lucide-react';
import { ContactCTA } from '../components/ContactCTA';
export function FinancingPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const benefits = [{
    icon: Banknote,
    title: 'Low Monthly Payments',
    desc: 'Spread the cost of your exterior project into manageable monthly installments that fit your budget.'
  }, {
    icon: Clock,
    title: 'Quick Approval Process',
    desc: 'Our financing partners offer fast decisions so you can get your project started without delay.'
  }, {
    icon: CheckCircle2,
    title: 'Multiple Options Available',
    desc: 'We work with several financing providers to find the best terms and rates for your situation.'
  }];
  const steps = [{
    number: '1',
    title: 'Contact Us',
    desc: "Reach out for your free estimate and mention you're interested in financing options."
  }, {
    number: '2',
    title: 'Discuss Options',
    desc: "We'll walk you through available financing plans and help you find the right fit."
  }, {
    number: '3',
    title: 'Get Started',
    desc: "Once approved, we schedule your project and get to work — it's that simple."
  }];
  return <div className="w-full bg-white" data-id="element-747">
      {/* Hero */}
      <section className="relative min-h-[35vh] flex items-center pt-20 pb-12 overflow-hidden bg-brand-gray-dark" data-id="element-748">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full relative z-10" data-id="element-749">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-750">
            <h1 className="font-heading font-black text-4xl md:text-5xl text-white mb-4" data-id="element-751">
              Flexible{' '}
              <span className="text-brand-blue" data-id="element-752">Financing Options</span>
            </h1>
            <p className="font-body text-xl text-gray-300 max-w-2xl" data-id="element-753">
              Don't let budget constraints delay important exterior repairs. We
              offer financing to make your project affordable.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20" data-id="element-754">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-755">
          <CreditCard size={48} className="text-brand-blue mx-auto mb-6" data-id="element-756" />
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-brand-gray-dark mb-6" data-id="element-757">
            Make Your Exterior Project More Affordable
          </h2>
          <p className="font-body text-lg text-brand-gray leading-relaxed max-w-3xl mx-auto mb-4" data-id="element-758">
            We understand that a new roof, siding, or gutter system is a
            significant investment. That's why we offer flexible financing
            options to help homeowners across Macomb, St. Clair, and Oakland
            counties protect their homes without financial stress.
          </p>
          <p className="font-body text-lg text-brand-gray leading-relaxed max-w-3xl mx-auto" data-id="element-759">
            Reach out today to learn more about what's available for your
            project. Our team will walk you through the options and help you
            find a plan that works for your budget.
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-gray-50" data-id="element-760">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-761">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-762">
            {benefits.map((benefit, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 text-center" data-id="element-763">
                <div className="w-16 h-16 bg-brand-blue/10 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-764">
                  <benefit.icon size={32} className="text-brand-blue" data-id="element-765" />
                </div>
                <h3 className="font-heading font-bold text-xl text-brand-gray-dark mb-3" data-id="element-766">
                  {benefit.title}
                </h3>
                <p className="font-body text-brand-gray" data-id="element-767">{benefit.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white" data-id="element-768">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-769">
          <h2 className="font-heading font-bold text-3xl text-brand-gray-dark mb-12 text-center" data-id="element-770">
            How It Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-771">
            {steps.map((step, index) => <div key={index} className="text-center" data-id="element-772">
                <div className="w-16 h-16 rounded-full bg-brand-blue text-white font-heading font-bold text-2xl flex items-center justify-center mx-auto mb-6 shadow-lg" data-id="element-773">
                  {step.number}
                </div>
                <h3 className="font-heading font-bold text-xl text-brand-gray-dark mb-3" data-id="element-774">
                  {step.title}
                </h3>
                <p className="font-body text-brand-gray" data-id="element-775">{step.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-brand-blue text-center" data-id="element-776">
        <div className="max-w-3xl mx-auto px-4" data-id="element-777">
          <h2 className="font-heading font-bold text-3xl text-white mb-6" data-id="element-778">
            Ready to Discuss Financing?
          </h2>
          <p className="font-body text-xl text-blue-100 mb-8" data-id="element-779">
            Contact us today and we'll help you find the right financing option
            for your project.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-780">
            <a href="tel:5862033701" className="bg-white text-brand-blue font-body font-bold text-lg py-4 px-10 rounded-lg hover:bg-gray-50 transition-colors shadow-lg flex items-center justify-center gap-2" data-id="element-781">
              <Phone size={20} data-id="element-782" /> Call (586) 203-3701
            </a>
            <a href="#quote" className="bg-transparent border-2 border-white text-white font-body font-bold text-lg py-4 px-10 rounded-lg hover:bg-white/10 transition-colors flex items-center justify-center gap-2" data-id="element-783">
              <MessageSquare size={20} data-id="element-784" /> Request a Quote
            </a>
          </div>
        </div>
      </section>

      <ContactCTA data-id="element-785" />
    </div>;
}